import { useQuery, type QueryStatus } from "@tanstack/react-query";

import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { message } from "@/lib/utils";
import { chartBgColors } from "@/lib/chart";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";

interface AgeItem {
  label: string;
  percentage: number;
}

function AgeDistribution({ items }: { items: AgeItem[] }) {
  if (!items.length) return <LockedContent message={message} />;

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={item.label} className="flex items-center gap-3">
          <p className="w-10 shrink-0 text-sm text-foreground">{item.label}</p>
          <Progress value={item.percentage} className="h-2 flex-1 bg-secondary" indicatorClassName={chartBgColors[index % chartBgColors.length]} />
          <p className="w-10 shrink-0 text-right text-sm text-muted-foreground">{item.percentage} %</p>
        </div>
      ))}
    </div>
  );
}

function DemographicsContent({ status, items }: { status: QueryStatus; items: AgeItem[] }) {
  switch (status) {
    case "pending":
      return <DemographicsSkeleton />;
    case "error":
      return <LockedContent message={message} />;
    case "success":
      return <AgeDistribution items={items} />;
  }
}

export function Demographics() {
  const { data, status } = useQuery(BrandIntelApiFactory.Queries.FetchTargetAudience());

  const items = (data?.target_audience?.age_distribution ?? [])
    .filter((item) => item.percentage > 0)
    .map((item) => ({ label: item.age_range, percentage: Math.round(item.percentage) }));

  return (
    <Card className="relative overflow-hidden flex flex-col h-[280px]">
      <CardHeader className="shrink-0">
        <CardTitle className="text-base font-semibold">Demographics</CardTitle>
      </CardHeader>
      <CardContent className="relative pt-0 space-y-4 flex-1 flex flex-col overflow-y-auto scrollbar-hidden">
        <DemographicsContent status={status} items={items} />
      </CardContent>
    </Card>
  );
}

function DemographicsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-4 w-10" />
          <Skeleton className="h-2 flex-1 rounded-full" />
          <Skeleton className="h-4 w-10" />
        </div>
      ))}
    </div>
  );
}
